import React, { useState } from "react";
import { motion } from "framer-motion";
import Keyword from "./components/Keyword";
import BookAuthorInfo from "../../common/controls/BookAuthorInfo";
import BookIntro from "./components/BookIntro";
import BookSectionCard from "./components/BookSectionCard";
import BookDescription from "./components/BookDescription";
import BookReaderMessage from "./components/BookReaderMessage";
import BookIndex from "./components/BookIndex";
import { indexWrapCss, creativeProcessCss } from "./css/Books.style";
import Header from "../../common/layouts/header/Header";
import Footer from "../../common/layouts/footer/Footer";

interface BookPageProps {
    bookData: any;
    onLike: () => void;
    likeCount: number;
    isMobile: boolean;
}

const figureRegex = /<figure[^>]*>[\s\S]*?<\/figure>/g;

/** 섹션 전체를 y:60으로 슬라이드업 */
const Section = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => (
    <motion.div
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.65, delay, ease: "easeOut" }}
    >
        {children}
    </motion.div>
);

/** 기획배경/창작과정/차별점용 — opacity만 페이드인 (내부 title/body가 각각 방향 애니메이션) */
const FadeSection = ({ children }: { children: React.ReactNode }) => (
    <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
    >
        {children}
    </motion.div>
);

/** 창작 과정 — 텍스트 본문 + 이미지 왼쪽부터 순서대로 stagger */
const CreativeProcessContent: React.FC<{ html: string }> = ({ html }) => {
    const figures = html.match(figureRegex) || [];
    const textContent = html.replace(figureRegex, '').trim();

    return (
        <div css={creativeProcessCss}>
            {textContent && (
                <div dangerouslySetInnerHTML={{ __html: textContent }} />
            )}
            {figures.length > 0 && (
                <div className="ck-figure-row ">
                    {figures.map((figHtml, i) => (
                        <motion.div
                            key={i}
                            className="fig-wrapper w-[300px] max-w-[300px]  "
                            initial={{ opacity: 0, x: -40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.45, delay: i * 0.45, ease: "easeOut" }}
                            dangerouslySetInnerHTML={{ __html: figHtml }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const BookPageLayout: React.FC<BookPageProps> = ({ bookData, onLike, likeCount, isMobile }) => {
    const [search, setSearch] = useState("");

    const onSearchHandler = (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value);

    const authorStatus: string | null = localStorage.getItem("authorStatus");
    const authorEmail: string | null = localStorage.getItem("email");
    const authorYn: boolean = !!authorStatus && authorEmail === bookData.author.authorEmail;

    const { keyword1, keyword1Reason, keyword2, keyword2Reason, keyword3, keyword3Reason } = bookData;
    const keywordData = [
        { keyword: keyword1 ?? "", content: keyword1Reason ?? "" },
        { keyword: keyword2 ?? "", content: keyword2Reason ?? "" },
        { keyword: keyword3 ?? "", content: keyword3Reason ?? "" },
    ].filter(item => item.keyword !== "");

    return (
        <div css={indexWrapCss}>
            <Header onChange={onSearchHandler} searchValue={search} name="search" activeFlag="" />

            {/* 히어로 — 자체 페이드인 */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
            >
                <BookIntro bookData={bookData} onLike={onLike} authorYn={authorYn} />
            </motion.div>

            <div
                className={
                    isMobile
                        ? 'flex flex-col gap-[60px] px-5 py-[60px]'
                        : 'mx-auto max-w-[1200px] w-full flex flex-col gap-[60px] py-[50px] px-[50px]'
                }
            >
                {/* 작가 PICK 키워드 */}
                {keywordData.length > 0 && (
                    <Section>
                        <Keyword data={keywordData} />
                    </Section>
                )}

                {/* 작품설명 */}
                <Section>
                    <BookDescription description={bookData.bookDescription} />
                </Section>

                {/* 작품 기획 배경 — title 왼→오, 본문 아래→위 */}
                {bookData.planningBackground && (
                    <FadeSection>
                        <BookSectionCard title="작품 기획 배경">
                            <p className="whitespace-pre-line">{bookData.planningBackground}</p>
                        </BookSectionCard>
                    </FadeSection>
                )}

                {/* 작품 창작 과정 — title 왼→오, 본문 아래→위, 이미지 왼쪽부터 순서대로 */}
                {bookData.creationProcess && (
                    <FadeSection>
                        <BookSectionCard title="작품 창작 과정">
                            <CreativeProcessContent html={bookData.creationProcess} />
                        </BookSectionCard>
                    </FadeSection>
                )}

                {/* 이전 작품과의 차별점 — title 왼→오, 본문 아래→위 */}
                {bookData.differenceFromPreviousWork && (
                    <FadeSection>
                        <BookSectionCard title="이전 작품과의 차별점">
                            <p className="whitespace-pre-line">{bookData.differenceFromPreviousWork}</p>
                        </BookSectionCard>
                    </FadeSection>
                )}

                {/* 독자에게 전하는 메시지 */}
                {bookData.messageToReaders && (
                    <Section>
                        <BookReaderMessage message={bookData.messageToReaders} />
                    </Section>
                )}

                {/* 작가 정보 */}
                <Section>
                    <BookAuthorInfo
                        id={bookData.author.authorId}
                        name={bookData.author.authorName}
                        desc={bookData.author.authorDescription}
                        email={bookData.author.authorEmail}
                        profile={bookData.author.profilePhoto}
                    />
                </Section>

                {/* 목차 */}
                <Section>
                    <BookIndex index={bookData.bookIndex} />
                </Section>
            </div>

            <Footer />
        </div>
    );
};

export default BookPageLayout;
